// ============================================================================
// LA COPIE AU CLIC — le rendu, et la promesse qu'il tient.
//
// ⚠️ CE COMPOSANT EXISTE PARCE QUE LES TROIS COPIES DU DÉPÔT ÉCHOUENT EN HTTP, et qu'aucune ne le dit
// pareil : l'une avale l'échec dans un `catch` vide, une autre émet son toast de succès AVANT le
// résultat, la troisième teste `navigator.clipboard` mais n'a aucun repli. Une primitive qui rendrait
// un simple `onClick` laisserait chaque app réécrire le même défaut — d'où un composant qui FAIT la
// copie, en lit le résultat, et affiche l'échec de lui-même.
//
// ⚠️ COPIER N ACTIVE PAS CE QUI EST DESSOUS. Le clic ne remonte pas : dans une ligne cliquable,
// copier un code ouvrait AUSSI la fiche. La garde vit ICI et non chez l appelant — sinon chaque ecran
// qui rend une ligne cliquable doit y penser, et le premier qui l oublie livre le defaut.
//
// ⚠️ LE REPLI MANUEL DOIT RESTER VRAI : l'appelant garde la valeur sélectionnable à côté. Quand rien
// ne marche, le message dit « sélectionnez la valeur » — il faut donc qu'on puisse.
// ============================================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, ReactNode } from 'react';
import { COPY_FEEDBACK, copyWith, Z_LAYERS, type CopyResult } from '@orgazone/ui-tokens';
import { Icon } from './icons';

/** Le presse-papier du navigateur, plus son repli — lu ICI, pour que la chaîne reste testable ailleurs. */
function envNavigateur() {
  return {
    writeText: typeof navigator !== 'undefined' && navigator.clipboard
      ? (v: string) => navigator.clipboard.writeText(v)
      : undefined,
    // Repli historique : il fonctionne en HTTP, là où `navigator.clipboard` n'existe même pas.
    // ⚠️ `setSelectionRange` en plus de `select()` : sans lui, iOS ne sélectionne rien.
    // ⚠️ LE FOCUS EST RENDU, ET LA ZONE TOUJOURS RETIRÉE. `select()` FOCALISE le textarea ; sans
    // restitution, le bouton perdait le focus à chaque copie — et comme ce chemin est le chemin
    // NORMAL en HTTP, l'utilisateur au clavier repartait de zéro à chaque code copié. Le `finally`
    // couvre le cas où `execCommand` lève : sans lui, un textarea invisible restait dans la page,
    // avec le focus.
    legacyCopy: typeof document === 'undefined' ? undefined : (v: string) => {
      const avant = document.activeElement as HTMLElement | null;
      const zone = document.createElement('textarea');
      zone.value = v;
      zone.readOnly = true;
      zone.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
      document.body.appendChild(zone);
      try {
        zone.select();
        zone.setSelectionRange(0, v.length);
        return document.execCommand('copy');
      } finally {
        zone.remove();
        avant?.focus({ preventScroll: true });
      }
    },
    secureContext: typeof window !== 'undefined' && window.isSecureContext,
  };
}

/**
 * L'état de copie, PORTÉ PAR LA VALEUR et non par un booléen.
 * ⚠️ Deux boutons voisins peuvent partager ce crochet sans s'annuler, et deux copies rapprochées ne
 * font pas clignoter la première : le minuteur précédent est annulé avant d'être reprogrammé.
 */
export function useCopy() {
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (minuteur.current) clearTimeout(minuteur.current); }, []);

  const [ancre, setAncre] = useState<Ancre | null>(null);

  const copy = useCallback(async (value: string, depuis?: HTMLElement | null): Promise<CopyResult> => {
    // ⚠️ L'ancre est MESURÉE AVANT l'attente : après un `await`, la page a pu défiler, et la bulle
    // apparaîtrait à côté de ce qu'elle confirme.
    if (depuis) {
      const r0 = depuis.getBoundingClientRect();
      setAncre({ x: r0.left + r0.width / 2, y: r0.top });
    }
    const r = await copyWith(envNavigateur(), value);
    if (minuteur.current) clearTimeout(minuteur.current);
    if (r.ok) {
      setError(null);
      setCopiedValue(value);
      minuteur.current = setTimeout(() => { setCopiedValue(null); setAncre(null); }, COPY_FEEDBACK.bubble.holdMs);
    } else {
      // ⚠️ L'ÉTAT DE SUCCÈS N'EST PAS APPLIQUÉ : c'est tout l'objet de cette primitive.
      setCopiedValue(null);
      setError(r.message);
      minuteur.current = setTimeout(() => { setError(null); setAncre(null); }, COPY_FEEDBACK.errMs);
    }
    return r;
  }, []);

  return { copy, copiedValue, error, ancre };
}

/**
 * LE STYLE QU'UN APPELANT PEUT DONNER À `Copyable` — tout, sauf ses marges.
 * ⚠️ LA ZONE DE CLIC EST UNE PAIRE : une marge intérieure qui l'élargit, une marge négative qui la compense. En retirer
 *    une moitié (`padding: 0` seul, la tentation naturelle pour « coller » le texte) décale le texte de 5 px : il passait
 *    sous l'icône de code-barres de l'en-tête de la fiche article et sortait de l'alignement du titre, sur six écrans. La
 *    paire laisse déjà la boîte à l'encombrement du texte : il n'y a rien à corriger chez l'appelant, et le typage le dit.
 */
export type CopyableStyle = Omit<CSSProperties,
  | 'padding' | 'paddingTop' | 'paddingRight' | 'paddingBottom' | 'paddingLeft' | 'paddingInline' | 'paddingBlock'
  | 'margin' | 'marginTop' | 'marginRight' | 'marginBottom' | 'marginLeft' | 'marginInline' | 'marginBlock'>;

export interface CopyableProps {
  /** La valeur RÉELLEMENT copiée — jamais ce qui est affiché, qui peut être tronqué. */
  value: string;
  /** Rendu visible. À défaut, la valeur elle-même. */
  children?: ReactNode;
  /** « Copier le gencode » — devient l'infobulle ET le libellé accessible. */
  label: string;
  /** Route le résultat vers le canal de l'app (toast, alerte). Le retour ancré s'affiche de toute façon. */
  onResult?: (r: CopyResult) => void;
  style?: CSSProperties;
}

/**
 * Valeur copiable EN LIGNE (cellule de tableau) — l'icône s'efface au repos.
 * ⚠️ L'icône revient au survol ET au focus clavier : au survol seul, elle reste fantôme pour qui
 * navigue au clavier, donc l'affordance n'existe pas.
 */
export function Copyable({ value, children, label, onResult, style }: Omit<CopyableProps, 'style'> & { style?: CopyableStyle }) {
  const { copy, copiedValue, error, ancre } = useCopy();
  const [actif, setActif] = useState(false);
  const copie = copiedValue === value;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
      <button
        type="button"
        title={label}
        aria-label={label}
        onClick={(e) => { e.stopPropagation(); void copy(value, e.currentTarget).then((r) => onResult?.(r)); }}
        onMouseEnter={() => setActif(true)}
        onMouseLeave={() => setActif(false)}
        onFocus={() => setActif(true)}
        onBlur={() => setActif(false)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: COPY_FEEDBACK.gap, minWidth: 0,
          border: 'none', background: 'transparent', color: 'inherit', font: 'inherit',
          borderRadius: 'var(--r-xs)', cursor: 'copy', textAlign: 'left',
          ...style,
          // APRÈS le style de l'appelant : la paire ne se défait pas, même par un objet construit hors du typage.
          padding: COPY_FEEDBACK.hitPad, margin: COPY_FEEDBACK.hitMargin,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children ?? value}</span>
        <Icon
          name={copie ? 'check' : 'copy'}
          size={COPY_FEEDBACK.icon}
          color={copie ? 'var(--ok-fg)' : 'currentColor'}
          style={{ flexShrink: 0, opacity: actif || copie ? 1 : COPY_FEEDBACK.iconRestOpacity }}
        />
      </button>
      {/* ⚠️ RÉGION LIVE : sans elle, le changement d'icône d'un bouton déjà focalisé n'est pas
          reverbalisé — la copie réussit sans que personne ne l'apprenne. Aucune des trois copies de
          production ne l'avait. Elle reste INVISIBLE : c'est la bulle qui parle à l'œil. */}
      <Annonce copie={copie} error={error} />
      {ancre && (copie || error) && (
        <Bulle ancre={ancre} erreur={Boolean(error)} texte={error ?? '\u2713 Copié'} />
      )}
    </span>
  );
}

/**
 * Bouton de copie À LIBELLÉ — le libellé bascule (« Copier » → « Copié »).
 * ⚠️ Le libellé de succès n'est appliqué que si la copie a RÉUSSI ; en cas d'échec le bouton reprend
 * son libellé de départ et le message réel s'affiche à côté.
 */
export function CopyButton({ value, label, idleLabel, doneLabel = 'Copié', onResult, className, style }: CopyableProps & {
  idleLabel: string;
  doneLabel?: string;
  className?: string;
}) {
  const { copy, copiedValue, error, ancre } = useCopy();
  const copie = copiedValue === value;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        className={className}
        title={label}
        onClick={(e) => { e.stopPropagation(); void copy(value, e.currentTarget).then((r) => onResult?.(r)); }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: COPY_FEEDBACK.gap, ...style }}
      >
        <Icon name={copie ? 'check' : 'copy'} size={COPY_FEEDBACK.icon} color={copie ? 'var(--ok-fg)' : 'currentColor'} />
        {copie ? doneLabel : idleLabel}
      </button>
      <Annonce copie={copie} error={error} />
      {ancre && (copie || error) && (
        <Bulle ancre={ancre} erreur={Boolean(error)} texte={error ?? '\u2713 Copié'} />
      )}
    </span>
  );
}

/** Où poser la bulle : le centre du déclencheur, en coordonnées de fenêtre. */
interface Ancre { x: number; y: number }

/**
 * L'INFOBULLE DE CONFIRMATION — « ✓ Copié », recette de la maquette.
 *
 * ⚠️ EN PORTAIL, hors du flux : elle doit apparaître au-dessus du déclencheur où qu'il soit, y compris
 * dans une case de 82 px et dans une cellule de tableau qui CLIPPE son contenu. Rendue en flux, elle
 * poussait la mise en page — et le message d'échec, qui dure six secondes, la faisait exploser.
 * ⚠️ `pointer-events: none` : elle ne doit jamais intercepter le clic suivant.
 * ⚠️ Elle ne remplace PAS l'annonce au lecteur d'écran : une bulle est une information visuelle.
 */
function Bulle({ ancre, texte, erreur }: { ancre: Ancre; texte: string; erreur: boolean }) {
  const [entre, setEntre] = useState(false);
  // Le fondu part au rendu suivant : posée déjà visible, la bulle apparaîtrait d'un coup.
  useEffect(() => { const t = requestAnimationFrame(() => setEntre(true)); return () => cancelAnimationFrame(t); }, []);
  const b = COPY_FEEDBACK.bubble;
  return createPortal(
    <div
      role="presentation"
      style={{
        position: 'fixed', left: ancre.x, top: Math.max(8, ancre.y - b.offsetY),
        zIndex: Z_LAYERS.toast, pointerEvents: 'none', whiteSpace: 'nowrap',
        padding: `${b.padV}px ${b.padH}px`, borderRadius: b.radius,
        fontSize: b.fontSize, fontWeight: b.weight,
        background: erreur ? 'var(--danger-ink)' : 'var(--ink-900)',
        color: 'var(--paper)',
        boxShadow: '0 8px 22px -6px rgba(0,0,0,.35)',
        opacity: entre ? 1 : 0,
        transform: `translate(-50%, ${entre ? 0 : 3}px)`,
        transition: `opacity ${b.fadeMs}ms ease, transform ${b.fadeMs}ms ease`,
        maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis',
      }}
    >{texte}</div>,
    document.body,
  );
}

/** Ce que la copie a produit — au lecteur d'écran toujours, à l'œil quand ça a échoué. */
/**
 * L'annonce au LECTEUR D'ÉCRAN — toujours invisible.
 * ⚠️ ELLE NE SE MONTRE PLUS (2026-08-16) : rendue en flux quand la copie échouait, elle occupait la
 * place pendant six secondes et faisait exploser la cellule qui l'hébergeait — dans une case de 82 px,
 * c'était garanti. Et ce chemin d'échec est le chemin NORMAL en HTTP. C'est la BULLE, hors flux, qui
 * parle à l'œil désormais ; celle-ci ne parle qu'à qui n'a pas d'yeux pour la voir.
 */
function Annonce({ copie, error }: { copie: boolean; error: string | null }) {
  return (
    <span role="status" aria-live="polite" className="sr-only">
      {error ?? (copie ? 'Copié dans le presse-papier.' : '')}
    </span>
  );
}
